import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AssignmentUnplannedListItemResponse,
  CreateAssignmentDateRequest,
  DutyModelWithTargetListItemResponse,
  CreatedResponse,
  CustomerAcceptRequest,
  CustomerAssignmentDateListItemResponse,
  CustomerAssignmentService,
  CustomerCreateAbsence,
  DatesCancelationRequest,
  EmptySuccessfulResponse,
  OrganizationFilterListResponse,
  PaginatedApiResponse,
  PspAcceptRequest,
  PspAssignmentDateListItemResponse,
  PspAssignmentService,
  PspCreateAbsence,
  UpdateWorkTimesRequest,
} from '../../../api';
import { BaseTimeRecordingItemInterface } from './_interfaces/base-time-recordings.interface';
import { BaseSharedHelperService } from '../../shared/services/base-shared-helper.service';
import {
  BaseTimeRecordCustomerExportInterface,
  BaseTimeRecordPspExportInterface,
} from './_interfaces/base-time-record-export.interface';
import { TimeRecordsPagination } from '../../shared/enums/base-list-pagination.enum';

type TimeRecordStatus =
  | 'open'
  | 'submitted'
  | 'accepted'
  | 'canceled'
  | 'absent'
  | 'corrected_by_customer'
  | 'corrected_by_psp'
  | 'automatically_accepted'
  | 'incomplete'
  | 'verification_needed';

const DEFAULT_TIME_RECORDS_STATUSES: TimeRecordStatus[] = [
  'open',
  'submitted',
  'accepted',
  'absent',
  'corrected_by_customer',
  'corrected_by_psp',
  'automatically_accepted',
  'incomplete',
  'verification_needed',
];

@Injectable({ providedIn: 'root' })
export class BaseTimeRecordingsService {
  constructor(
    private pspAssignmentService: PspAssignmentService,
    private customerAssignmentService: CustomerAssignmentService
  ) {}

  getPspTimeRecordsList(params: {
    psp: number;
    page?: any;
    size?: any;
    assignmentId?: any;
    statuses?: TimeRecordStatus[];
    period?: string;
    pspBranches?: any;
    profiles?: any;
    customerDepartments?: any;
    locations?: any;
    search?: string;
  }): Observable<
    PaginatedApiResponse & {
      data?: Array<PspAssignmentDateListItemResponse>;
      filters?: OrganizationFilterListResponse;
    }
  > {
    return this.pspAssignmentService.getPspAssignmentDateList({
      psp: params.psp,
      page: params.page || TimeRecordsPagination.page,
      size: params.size || TimeRecordsPagination.size,
      assignmentId: params.assignmentId,
      'statuses[]': params.statuses ?? DEFAULT_TIME_RECORDS_STATUSES,
      dateFrom: params.period?.split(',')[0],
      dateTo: params.period?.split(',')[1],
      'pspBranches[]': params.pspBranches,
      'profiles[]': params.profiles,
      'customerDepartments[]': params.customerDepartments,
      'customerLocations[]': params.locations,
      search: params.search,
    });
  }

  getCustomerTimeRecordsList(params: {
    customer: number;
    page?: any;
    size?: any;
    assignmentId?: any;
    statuses?: TimeRecordStatus[];
    period?: string;
    pspBranches?: any;
    profiles?: any;
    customerDepartments?: any;
    locations?: any;
    search?: string;
  }): Observable<
    PaginatedApiResponse & {
      data?: Array<CustomerAssignmentDateListItemResponse>;
      filters?: OrganizationFilterListResponse;
    }
  > {
    return this.customerAssignmentService.getCustomerAssignmentDateList({
      customer: params.customer,
      page: params.page || TimeRecordsPagination.page,
      size: params.size || TimeRecordsPagination.size,
      assignmentId: params.assignmentId,
      'statuses[]': params.statuses ?? DEFAULT_TIME_RECORDS_STATUSES,
      dateFrom: params.period?.split(',')[0],
      dateTo: params.period?.split(',')[1],
      'pspBranches[]': params.pspBranches,
      'profiles[]': params.profiles,
      'customerDepartments[]': params.customerDepartments,
      'customerLocations[]': params.locations,
      search: params.search,
    });
  }

  acceptCustomerTimeRecord(params: {
    customer: number;
    body: CustomerAcceptRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerAssignmentService.postAssignmentAssignmentDatesAcceptance(params);
  }

  acceptPspTimeRecord(params: { psp: number; body: PspAcceptRequest }): Observable<EmptySuccessfulResponse> {
    return this.pspAssignmentService.postPspAssignmentDateAccept(params);
  }

  updateCustomerTimeRecord(params: {
    customer: number;
    assignment: number;
    assignmentDate: number;
    body: UpdateWorkTimesRequest;
  }): Observable<CreatedResponse> {
    return this.customerAssignmentService.putCustomerAssignmentAssignmentDateWorkTimeUpdate(params);
  }

  updatePspTimeRecord(params: {
    psp: number;
    assignment: number;
    assignmentDate: number;
    body: UpdateWorkTimesRequest;
  }): Observable<CreatedResponse> {
    return this.pspAssignmentService.putPspAssignmentDateTimeReplace(params);
  }

  prepareTimeRecordsList(list: CustomerAssignmentDateListItemResponse[]): BaseTimeRecordingItemInterface[] {
    return list.map((item) => ({ ...item, isSelected: false }));
  }

  timeRecordsListToggleSelection(
    ids: number[],
    isSelected: boolean,
    list: BaseTimeRecordingItemInterface[]
  ): BaseTimeRecordingItemInterface[] {
    if (ids.length === 0) {
      return list.map((item) => {
        return {
          ...item,
          isSelected,
        };
      });
    }

    return list.map((item) => {
      if (ids.includes(item.id)) {
        return {
          ...item,
          isSelected,
        };
      }

      return item;
    });
  }

  createPspTimeRecordAbsence(params: {
    psp: number;
    assignment: number;
    body: PspCreateAbsence;
  }): Observable<EmptySuccessfulResponse> {
    return this.pspAssignmentService.postPspAssignmentAbsenceCreate(params);
  }

  createCustomerTimeRecordAbsence(params: {
    customer: number;
    assignment: number;
    body: CustomerCreateAbsence;
  }): Observable<EmptySuccessfulResponse> {
    return this.customerAssignmentService.postCustomerAssignmentAbsenceCreate(params);
  }

  exportCustomerTimeRecords(
    params: BaseTimeRecordCustomerExportInterface
  ): Observable<{ fileName: string; body: Blob | (null & Blob) }> {
    return this.customerAssignmentService.getCustomerAssignmentDateExport$Response(params).pipe(
      map((response) => {
        const contentDispositionValue = response.headers.get('Content-Disposition');
        const fileName = BaseSharedHelperService.fileNameFromHeaderValue(contentDispositionValue, '=');

        return {
          body: response.body,
          fileName,
        };
      })
    );
  }

  exportPspTimeRecords(
    params: BaseTimeRecordPspExportInterface
  ): Observable<{ fileName: string; body: Blob | (null & Blob) }> {
    return this.pspAssignmentService.getPspAssignmentDateExport$Response(params).pipe(
      map((response) => {
        const contentDispositionValue = response.headers.get('Content-Disposition');
        const fileName = BaseSharedHelperService.fileNameFromHeaderValue(contentDispositionValue, '=');

        return {
          body: response.body,
          fileName,
        };
      })
    );
  }

  customerAssignmentsForTimeRecordCreation(customer: number): Observable<AssignmentUnplannedListItemResponse[]> {
    return this.customerAssignmentService
      .getCustomerAsignmentListUnplanned({ customer })
      .pipe(map((response) => response.data));
  }

  pspAssignmentsForTimeRecordCreation(psp: number): Observable<AssignmentUnplannedListItemResponse[]> {
    return this.pspAssignmentService.getPspAsignmentListUnplanned({ psp }).pipe(map((response) => response.data));
  }

  getCustomerDutyModelOptions(params: {
    customer: number;
    assignment: number;
    assignmentDate: number;
  }): Observable<DutyModelWithTargetListItemResponse[]> {
    return this.customerAssignmentService
      .getCustomerAssignmentAssignmentDateDutyModelOptionsList(params)
      .pipe(map((response) => response.data || []));
  }

  getPspDutyModelOptions(params: {
    psp: number;
    assignment: number;
    assignmentDate: number;
  }): Observable<DutyModelWithTargetListItemResponse[]> {
    return this.pspAssignmentService
      .getPspAssignmentAssignmentDateDutyModelOptionsList(params)
      .pipe(map((response) => response.data || []));
  }

  createCustomerAssignmentDate(params: {
    customer: number;
    assignment: number;
    body: CreateAssignmentDateRequest;
  }): Observable<CreatedResponse> {
    return this.customerAssignmentService.postCustomerAsignmentAssignmentDatesCreate(params);
  }

  createPspAssignmentDate(params: {
    psp: number;
    assignment: number;
    body: CreateAssignmentDateRequest;
  }): Observable<CreatedResponse> {
    return this.pspAssignmentService.postPspAsignmentAssignmentDateCreate(params);
  }

  cancelAssignmentDates(params: {
    psp: number;
    assignment: number;
    body: DatesCancelationRequest;
  }): Observable<EmptySuccessfulResponse> {
    return this.pspAssignmentService.postPspAsignmentDatesCancelation(params);
  }
}
